import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  FilterIcon,
  InboxIcon,
  SearchIcon,
  XIcon,
} from "lucide-react";

import { useProject, useReports } from "../app/queries";
import { ProjectTabs } from "../components/ProjectTabs";
import { Topbar } from "../components/Topbar";
import { ProjectNotFound } from "../components/ErrorState";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../components/ui/empty";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "../components/ui/input-group";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Page, PageBody, PageHeader, PageTitle, PageDescription } from "../components/PageLayout";
import {
  formatExact,
  relativeTime,
  SeverityChip,
  StatusChip,
} from "../lib/format";
import { Badge } from "../components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  filterReports,
  sortReports,
  uniqueValues,
  type ReportFilters,
  type ReportSortField,
  type SortDirection,
} from "../lib/reportFilters";
import { ReportFilterControls } from "../components/ReportFilterControls";
import { cn } from "../lib/utils";
import type { ReportSummary } from "../lib/api";

export function ProjectReportsPage() {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const projects = useProject(id);
  const reports = useReports(id);
  const project = projects.project;
  const items = useMemo(() => reports.data?.reports ?? [], [reports.data]);

  const filters = useMemo<ReportFilters>(
    () => ({
      status: searchParams.get("status") ?? undefined,
      severity: searchParams.get("severity") ?? undefined,
      platform: searchParams.get("platform") ?? undefined,
      gameVersion: searchParams.get("gameVersion") ?? undefined,
      query: searchParams.get("q") ?? undefined,
    }),
    [searchParams],
  );

  const [sortField, setSortField] = useState<ReportSortField>("received");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const filtered = useMemo(() => {
    const f = filterReports(items, filters);
    return sortReports(f, sortField, sortDirection);
  }, [items, filters, sortField, sortDirection]);

  const platforms = useMemo(() => uniqueValues(items, "platform"), [items]);
  const versions = useMemo(() => uniqueValues(items, "gameVersion"), [items]);

  const [pendingQuery, setPendingQuery] = useState(filters.query ?? "");
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  // Refs let the debounced writer read the current URL + filter state
  // without re-firing on every change. We only want the timer to
  // re-arm when the user types a new pendingQuery.
  const searchParamsRef = useRef(searchParams);
  const setSearchParamsRef = useRef(setSearchParams);
  const filtersQueryRef = useRef(filters.query);
  searchParamsRef.current = searchParams;
  setSearchParamsRef.current = setSearchParams;
  filtersQueryRef.current = filters.query;

  useEffect(() => {
    setPendingQuery(filters.query ?? "");
  }, [filters.query]);
  useEffect(() => {
    const value = pendingQuery.trim();
    if (value === (filtersQueryRef.current ?? "")) return;
    const handle = setTimeout(() => {
      const next = new URLSearchParams(searchParamsRef.current);
      if (!value) next.delete("q");
      else next.set("q", value);
      setSearchParamsRef.current(next, { replace: true });
    }, 150);
    return () => clearTimeout(handle);
  }, [pendingQuery]);

  function setFilter<K extends keyof ReportFilters>(
    key: K,
    value: ReportFilters[K],
  ) {
    const next = new URLSearchParams(searchParams);
    const stringValue = typeof value === "string" ? value.trim() : "";
    if (!stringValue) next.delete(key);
    else next.set(key, stringValue);
    setSearchParams(next, { replace: true });
  }

  function toggleSort(field: ReportSortField) {
    if (field === sortField) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection(field === "received" ? "desc" : "asc");
    }
  }

  if (projects.isLoading) {
    return (
      <Page>
        <Topbar title="…" />
        <PageBody width="wide">
          <Skeleton className="h-10 w-1/3" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-72 w-full" />
        </PageBody>
      </Page>
    );
  }

  if (!project) {
    return (
      <ProjectNotFound
        breadcrumb={[
          { label: "Projects", to: "/" },
          { label: "Not found" },
        ]}
      />
    );
  }

  const hasFilters =
    !!filters.status ||
    !!filters.severity ||
    !!filters.platform ||
    !!filters.gameVersion ||
    !!(filters.query && filters.query.length > 0);
  const activeChips = (
    [
      filters.status && { key: "status", label: filters.status.replace("_", " ") },
      filters.severity && { key: "severity", label: filters.severity },
      filters.platform && { key: "platform", label: filters.platform },
      filters.gameVersion && { key: "gameVersion", label: filters.gameVersion },
      filters.query && { key: "q", label: `"${filters.query}"` },
    ] as Array<{ key: keyof ReportFilters; label: string } | false>
  ).filter((c): c is { key: keyof ReportFilters; label: string } => Boolean(c));

  return (
    <Page>
      <Topbar
        breadcrumb={[
          { label: "Projects", to: "/" },
          { label: project.name, to: `/projects/${id}` },
          { label: "Reports" },
        ]}
      />
      <ProjectTabs />
      <PageHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <PageTitle>Reports</PageTitle>
              <PageDescription>
                {reports.isLoading
                  ? "Loading reports…"
                  : hasFilters
                    ? `${filtered.length} of ${items.length} reports match`
                    : `${items.length} ${items.length === 1 ? "report" : "reports"}`}
              </PageDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <InputGroup className="w-56 sm:w-72">
                <InputGroupAddon align="inline-start">
                  <SearchIcon />
                </InputGroupAddon>
                <InputGroupInput
                  type="search"
                  value={pendingQuery}
                  onChange={(e) => setPendingQuery(e.target.value)}
                  placeholder="Search title or reference"
                  aria-label="Search reports"
                  data-testid="report-search"
                />
              </InputGroup>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="md:hidden"
                onClick={() => setFilterSheetOpen(true)}
              >
                <FilterIcon /> Filters
                {activeChips.length > 0 ? (
                  <Badge className="ml-1">{activeChips.length}</Badge>
                ) : null}
              </Button>
            </div>
          </div>

          <div
            className="hidden md:flex"
            data-testid="filter-bar"
          >
            <ReportFilterControls
              filters={filters}
              platforms={platforms}
              versions={versions}
              onChange={setFilter}
              layout="inline"
              className="w-full"
            />
          </div>

          {activeChips.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>Active filters</span>
              {activeChips.map(({ key, label }) => (
                <Badge
                  key={key as string}
                  render={
                    <button
                      type="button"
                      onClick={() => setFilter(key, "")}
                      aria-label={`Remove ${label} filter`}
                    />
                  }
                  className="cursor-pointer gap-1 transition-colors hover:bg-accent"
                >
                  {label}
                  <XIcon aria-hidden="true" className="size-3" />
                </Badge>
              ))}
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto px-1 text-xs"
                onClick={() =>
                  setSearchParams(new URLSearchParams(), { replace: true })
                }
              >
                Clear all
              </Button>
            </div>
          ) : null}
        </div>
      </PageHeader>

      <PageBody width="wide">
        {reports.isError ? (
          <Alert variant="destructive">
            <AlertDescription>{reports.error.message}</AlertDescription>
          </Alert>
        ) : reports.isLoading ? (
          <Skeleton className="h-72 w-full" />
        ) : items.length === 0 ? (
          <EmptyReports projectKey={project.key} />
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border bg-card/60 px-4 py-10 text-center text-sm text-muted-foreground">
            <span>No reports match the current filters.</span>
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={() =>
                setSearchParams(new URLSearchParams(), { replace: true })
              }
            >
              Clear filters
            </Button>
          </div>
        ) : (
          <ReportsTable
            items={filtered}
            sortField={sortField}
            sortDirection={sortDirection}
            onToggleSort={toggleSort}
            onOpen={(reportId) =>
              navigate(`/projects/${id}/reports/${reportId}`)
            }
          />
        )}
      </PageBody>

      <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
        <SheetContent
          side="right"
          className="flex w-full max-w-sm flex-col gap-0 p-0"
        >
          <SheetHeader className="border-b border-border/60">
            <SheetTitle>Filters</SheetTitle>
            <SheetDescription>
              Refine the visible reports on this project.
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-5 py-5">
            <ReportFilterControls
              filters={filters}
              platforms={platforms}
              versions={versions}
              onChange={setFilter}
              layout="stacked"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setSearchParams(new URLSearchParams(), { replace: true })
              }
            >
              Clear filters
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </Page>
  );
}

function EmptyReports({ projectKey }: { projectKey: string }) {
  return (
    <Empty className="rounded-xl border bg-card">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <InboxIcon />
        </EmptyMedia>
        <EmptyTitle>No reports yet</EmptyTitle>
        <EmptyDescription>
          Reports submitted by your game clients will appear here. Mint an API
          key on the API keys page and send a multipart POST to{" "}
          <span className="font-mono">/api/v1/reports</span>.
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent className="max-w-xl">
        <pre className="w-full overflow-x-auto rounded-md border border-border bg-background px-3 py-2 text-left font-mono text-xs text-muted-foreground">
          <code>{`curl -X POST $BASE/api/v1/reports \\
  -H "Authorization: Bearer <token>" \\
  -F 'report={"externalId":"${projectKey}-1","title":"hello","severity":"low"};type=application/json' \\
  -F files=@./shot.png`}</code>
        </pre>
      </EmptyContent>
    </Empty>
  );
}

function ReportsTable({
  items,
  sortField,
  sortDirection,
  onToggleSort,
  onOpen,
}: {
  items: ReportSummary[];
  sortField: ReportSortField;
  sortDirection: SortDirection;
  onToggleSort: (field: ReportSortField) => void;
  onOpen: (id: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <SortableTh
              field="title"
              current={sortField}
              direction={sortDirection}
              onToggle={onToggleSort}
              className="min-w-[16rem] sm:w-[34%]"
            >
              Title
            </SortableTh>
            <TableHead className="hidden w-[12%] sm:table-cell">
              Reference
            </TableHead>
            <SortableTh
              field="status"
              current={sortField}
              direction={sortDirection}
              onToggle={onToggleSort}
              className="w-[12%]"
            >
              Status
            </SortableTh>
            <SortableTh
              field="severity"
              current={sortField}
              direction={sortDirection}
              onToggle={onToggleSort}
              className="hidden w-[12%] lg:table-cell"
            >
              Severity
            </SortableTh>
            <TableHead className="hidden w-[10%] lg:table-cell">
              Version
            </TableHead>
            <SortableTh
              field="received"
              current={sortField}
              direction={sortDirection}
              onToggle={onToggleSort}
              className="w-[14%]"
            >
              Received
            </SortableTh>
            <TableHead className="hidden w-[8%] text-right sm:table-cell">
              Files
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((r) => (
            <ReportTableRow key={r.id} row={r} onOpen={onOpen} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function SortableTh({
  field,
  current,
  direction,
  onToggle,
  className,
  children,
}: {
  field: ReportSortField;
  current: ReportSortField;
  direction: SortDirection;
  onToggle: (field: ReportSortField) => void;
  className?: string;
  children: React.ReactNode;
}) {
  const isActive = field === current;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onToggle(field)}
        aria-sort={
          isActive ? (direction === "asc" ? "ascending" : "descending") : "none"
        }
        className={cn(
          "inline-flex items-center gap-1 text-xs uppercase tracking-[0.12em]",
          isActive
            ? "text-foreground"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        {children}
        {isActive ? (
          <span aria-hidden="true">{direction === "asc" ? "↑" : "↓"}</span>
        ) : (
          <span aria-hidden="true" className="text-muted-foreground/60">
            ↕
          </span>
        )}
      </button>
    </TableHead>
  );
}

function ReportTableRow({
  row,
  onOpen,
}: {
  row: ReportSummary;
  onOpen: (id: string) => void;
}) {
  return (
    <TableRow
      onClick={() => onOpen(row.id)}
      className="cursor-pointer"
      data-testid="report-row"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(row.id);
        }
      }}
    >
      <TableCell>
        <div className="flex flex-col gap-0.5">
          <span className="line-clamp-2 text-sm font-medium text-foreground">
            {row.title || "Untitled report"}
          </span>
          <span className="font-mono text-[11px] text-muted-foreground sm:hidden">
            {row.reference}
          </span>
          <div className="flex flex-wrap items-center gap-1.5 lg:hidden">
            <SeverityChip severity={row.severity} />
            <span className="font-mono text-[11px] text-muted-foreground">
              {row.gameVersion || "—"}
            </span>
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden font-mono text-xs text-muted-foreground sm:table-cell">
        {row.reference}
      </TableCell>
      <TableCell>
        <StatusChip status={row.status} />
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <SeverityChip severity={row.severity} />
      </TableCell>
      <TableCell className="hidden font-mono text-xs text-muted-foreground lg:table-cell">
        {row.gameVersion || "—"}
      </TableCell>
      <TableCell className="text-muted-foreground">
        <span
          className="font-mono text-xs"
          title={formatExact(row.receivedAt)}
          data-testid="report-received"
        >
          {relativeTime(row.receivedAt)}
        </span>
      </TableCell>
      <TableCell className="hidden text-right font-mono tabular-nums text-xs text-muted-foreground sm:table-cell">
        {row.attachmentCount}
      </TableCell>
    </TableRow>
  );
}
